"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import { useParams, useRouter } from "next/navigation"
import Link from "next/link"
import { Clock, Facebook, Twitter, Mail, MessageCircle } from "lucide-react"
import { HomePageLayout } from "@/components/website/homepage-layout"
import { ViralNewsSidebar } from "@/components/website/viral-news-sidebar"
import { ImageSlider } from "@/components/website/image-slider"
import { LatestNewsList } from "@/components/website/latest-news-list"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import type { Article, Category } from "@/lib/types"
import { replaceImageCodesWithImages } from "@/lib/image-code-utils"
import { processPromotedArticles } from "@/lib/promoted-article-utils"
import { ensureCmsPromotedArticleStyles } from "@/lib/cms-promoted-article-css"
import { formatDaysSinceCreated } from "@/lib/format-days-since"

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "https://dev.oxygentamil.com/api"

const HOME_FEED_INITIAL = 5

// NOTE: This is essentially the previous ArticlePage implementation,
// moved into a dedicated client component so that the route-level
// page.tsx can be a Server Component with generateMetadata.

function ArticleContent({ content }: { content: string }) {
  const contentRef = useRef<HTMLDivElement>(null)
  const router = useRouter()

  useEffect(() => {
    ensureCmsPromotedArticleStyles()
    if (!contentRef.current) return

    contentRef.current.querySelectorAll("a > .cms-promoted-inner").forEach((inner) => {
      const a = inner.parentElement as HTMLAnchorElement | null
      if (!a || a.tagName !== "A") return
      if (a.getAttribute("data-cms-embed") === "promoted-article") return
      a.setAttribute("data-cms-embed", "promoted-article")
      a.classList.add(
        "promoted-article-preview",
        "cms-promoted-article",
        "not-prose",
        "promoted-article-link",
        "block",
        "no-underline",
      )
      a.style.cssText =
        "display:block;max-width:100%;width:100%;margin:1rem 0;text-decoration:none;color:inherit;cursor:pointer;box-sizing:border-box;"
      const href = a.getAttribute("href") || ""
      const m = href.match(/\/article\/([^/?#]+)/)
      const slug = m ? m[1] : ""
      if (slug) {
        a.setAttribute("href", `/article/${slug}`)
        a.onclick = (e: MouseEvent) => {
          e.preventDefault()
          router.push(`/article/${slug}`)
        }
      }
    })

    const promotedPreviews = contentRef.current.querySelectorAll<HTMLElement>(".promoted-article-preview")
    const wrappers = contentRef.current.querySelectorAll<HTMLElement>(".promoted-article-wrapper")

    promotedPreviews.forEach((preview) => {
      if (preview.tagName === "A") {
        const anchor = preview as HTMLAnchorElement
        const href = preview.getAttribute("href") || ""
        const slug = preview.getAttribute("data-article-slug") || href.replace("/article/", "") || ""

        if (slug) {
          preview.setAttribute("href", `/article/${slug}`)
        }

        anchor.onclick = (e: MouseEvent) => {
          e.preventDefault()
          const articleSlug =
            preview.getAttribute("data-article-slug") ||
            preview.getAttribute("href")?.replace("/article/", "") ||
            ""
          if (articleSlug) {
            router.push(`/article/${articleSlug}`)
          }
        }

        preview.setAttribute("data-cms-embed", "promoted-article")
        preview.className =
          "promoted-article-preview cms-promoted-article not-prose promoted-article-link block no-underline"
        preview.style.cssText =
          "display:block;max-width:100%;width:100%;margin:1rem 0;text-decoration:none;color:inherit;cursor:pointer;box-sizing:border-box;"

        const imageUrl = preview.getAttribute("data-article-image") || ""
        const thumb =
          preview.querySelector(".cms-promoted-thumb") || preview.querySelector("div div:first-child")
        const img = thumb?.querySelector("img")
        if (img && imageUrl) {
          const absoluteUrl = imageUrl.startsWith("http")
            ? imageUrl
            : `${process.env.NEXT_PUBLIC_API_URL?.replace("/api", "") || "https://dev.oxygentamil.com"}${imageUrl}`
          img.src = absoluteUrl
        }
        return
      }
    })

    const origin = process.env.NEXT_PUBLIC_API_URL?.replace("/api", "") || "https://dev.oxygentamil.com"
    wrappers.forEach((wrapper) => {
      const slug = wrapper.getAttribute("data-slug") || ""
      if (!slug) return

      const linkWrapper = document.createElement("a")
      linkWrapper.href = `/article/${slug}`
      linkWrapper.setAttribute("data-cms-embed", "promoted-article")
      linkWrapper.className = "cms-promoted-article not-prose promoted-article-link block no-underline"
      linkWrapper.style.cssText =
        "display:block;max-width:100%;width:100%;margin:1rem 0;text-decoration:none;color:inherit;cursor:pointer;box-sizing:border-box;"
      linkWrapper.onclick = (e) => {
        e.preventDefault()
        router.push(`/article/${slug}`)
      }

      linkWrapper.innerHTML = wrapper.innerHTML
      const imageUrl = wrapper.getAttribute("data-image") || ""
      const img = linkWrapper.querySelector("img")
      if (img && imageUrl && !imageUrl.startsWith("http")) {
        img.src = `${origin}${imageUrl.startsWith("/") ? "" : "/"}${imageUrl.replace(/^\//, "")}`
      }
      wrapper.replaceWith(linkWrapper)
    })
  }, [content])

  return (
    <div
      ref={contentRef}
      className="article-content-html prose prose-lg max-w-none text-foreground"
      style={{ isolation: "isolate" }}
      dangerouslySetInnerHTML={{ __html: content }}
    />
  )
}

export default function ArticlePageClient() {
  const params = useParams()
  const router = useRouter()
  const slug = params?.slug as string
  const [article, setArticle] = useState<Article | null>(null)
  const [processedContent, setProcessedContent] = useState<string>("")
  const [importantNews, setImportantNews] = useState<Article[]>([])
  const [viralNews, setViralNews] = useState<Article[]>([])
  const [latestNews, setLatestNews] = useState<Article[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [importantPage, setImportantPage] = useState(1)
  const [hasMoreImportant, setHasMoreImportant] = useState(true)
  const [loadingMoreImportant, setLoadingMoreImportant] = useState(false)
  const [homeFeedExpanded, setHomeFeedExpanded] = useState(false)

  useEffect(() => {
    setHomeFeedExpanded(false)
  }, [slug])

  const normalizeList = (data: any) => {
    const list =
      (Array.isArray(data) && data) ||
      data?.items ||
      data?.data ||
      data?.results ||
      data?.articles ||
      []
    return (Array.isArray(list) ? list : []).map((a: any) => ({
      ...a,
      id: String(a.id),
    }))
  }

  const safeJson = async (res: Response) => {
    try {
      return await res.json()
    } catch (e) {
      console.error("[ArticlePage] JSON parse failed:", { url: res.url, status: res.status }, e)
      return null
    }
  }

  const fetchImportantPage = useCallback(
    async (page: number) => {
      // Important News uses viral endpoint (different from Latest News which uses /latest)
      const urlWithPage = `${API_BASE}/public/articles/viral?limit=10&page=${page}`
      const urlWithOffset = `${API_BASE}/public/articles/viral?limit=10&offset=${(page - 1) * 10}`

      const tryFetch = async (url: string) => {
        const res = await fetch(url)
        if (!res.ok) return []
        const json = await safeJson(res)
        return normalizeList(json)
      }

      let list = await tryFetch(urlWithPage)
      if (list.length === 0) list = await tryFetch(urlWithOffset)
      return list
    },
    [],
  )

  useEffect(() => {
    if (!slug) return

    const fetchData = async () => {
      try {
        const [articleRes, latestRes, viralRes, categoriesRes] = await Promise.all([
          fetch(`${API_BASE}/public/articles/${slug}`),
          fetch(`${API_BASE}/public/articles/latest?limit=10`),
          fetch(`${API_BASE}/public/articles/viral?limit=5`),
          fetch(`${API_BASE}/public/categories`),
        ])

        if (articleRes.status === 301) {
          const redirectData = await safeJson(articleRes)
          if (redirectData?.redirect && redirectData.redirect_to_slug) {
            router.replace(`/article/${redirectData.redirect_to_slug}`)
            return
          }
        }

        if (!articleRes.ok) throw new Error("Article not found")

        const articleData = articleRes.ok ? await safeJson(articleRes) : null
        const latestData = latestRes.ok ? await safeJson(latestRes) : []
        const viralData = viralRes.ok ? await safeJson(viralRes) : []
        const categoriesData = categoriesRes.ok ? await safeJson(categoriesRes) : []

        if (!articleData) throw new Error("Article payload missing")
        const articleObj = { ...articleData, id: String(articleData.id) }
        setArticle(articleObj)

        if (articleData.content) {
          let processed = await replaceImageCodesWithImages(articleData.content)
          if (typeof window !== "undefined") {
            processed = processPromotedArticles(processed)
          }
          setProcessedContent(processed)
        } else {
          setProcessedContent("")
        }

        const latestNormalized = normalizeList(latestData)
        const viralNormalized = normalizeList(viralData)
        const currentArticleId = String(articleObj.id)
        // Important News uses viral articles (different from Latest News which uses /latest)
        const importantWithoutCurrent = viralNormalized.filter(
          (a: any) => String(a.id) !== currentArticleId,
        )
        setLatestNews(latestNormalized)
        setImportantNews(importantWithoutCurrent.slice(0, 10))
        setImportantPage(1)
        setHasMoreImportant(true)
        const normalizedViral = normalizeList(viralData)
        setViralNews(normalizedViral.filter((a: any) => String(a.id) !== currentArticleId))
        setCategories(
          (Array.isArray(categoriesData) ? categoriesData : []).map((c: any) => ({
            ...c,
            id: String(c.id),
          })),
        )
      } catch (error) {
        console.error("Failed to fetch data:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [slug, router])

  const loadMoreImportant = useCallback(async () => {
    if (loadingMoreImportant || !hasMoreImportant) return
    if (!article) return
    setLoadingMoreImportant(true)
    try {
      const nextPage = importantPage + 1
      const nextList = await fetchImportantPage(nextPage)
      if (nextList.length === 0) {
        setHasMoreImportant(false)
        return
      }
      const currentArticleId = String(article.id)
      const filteredNext = nextList.filter((a: Article) => String(a.id) !== currentArticleId)
      setImportantNews((prev) => {
        const seen = new Set(prev.map((a) => a.id))
        const deduped = filteredNext.filter((a: Article) => !seen.has(a.id))
        return [...prev, ...deduped]
      })
      setImportantPage(nextPage)
    } finally {
      setLoadingMoreImportant(false)
    }
  }, [article, fetchImportantPage, hasMoreImportant, importantPage, loadingMoreImportant])

  useEffect(() => {
    const handleScroll = () => {
      if (loadingMoreImportant || !hasMoreImportant) return
      const scrollPosition = window.innerHeight + window.scrollY
      const threshold = document.documentElement.scrollHeight - 200
      if (scrollPosition >= threshold) {
        loadMoreImportant()
      }
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [loadMoreImportant, loadingMoreImportant, hasMoreImportant])

  const shareUrl = typeof window !== "undefined" ? window.location.href : ""

  if (loading) {
    return (
      <HomePageLayout categories={categories}>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
      </HomePageLayout>
    )
  }

  if (!article) {
    return (
      <HomePageLayout categories={categories}>
        <div className="container mx-auto px-4 py-12">
          <Card
            style={{
              display: "block",
              position: "relative",
              background: "#fff",
              borderRadius: "5px",
              boxShadow: "1px 1px 5px rgba(0, 0, 0, 0.12)",
            }}
          >
            <CardContent className="p-12 text-center">
              <h1 className="text-2xl font-bold mb-4">Article Not Found</h1>
              <p className="text-muted-foreground mb-6">
                The article you're looking for doesn't exist or has been removed.
              </p>
              <Link href="/">
                <Button>Go to Homepage</Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </HomePageLayout>
    )
  }

  const articleHomeFeedFiltered = latestNews.filter((a) => a.id !== article.id)
  const articleHomeFeedDisplayed = homeFeedExpanded
    ? articleHomeFeedFiltered
    : articleHomeFeedFiltered.slice(0, HOME_FEED_INITIAL)
  const showArticleHomeFeedMore =
    !homeFeedExpanded && articleHomeFeedFiltered.length > HOME_FEED_INITIAL

  return (
    <HomePageLayout categories={categories}>
      <div className="container mx-auto px-4 py-4">
        <div className="grid grid-cols-12 gap-4">
          <main className="col-span-12 lg:col-span-8">
            <div className="bg-white rounded-md shadow-sm px-4 py-4 md:px-6 md:py-5">
              <div className="mb-4">
                <h1
                  style={{
                    display: "block",
                    position: "relative",
                    fontSize: "20px",
                    lineHeight: "32px",
                    fontWeight: 700,
                  }}
                  className="mb-3 text-foreground"
                >
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3 text-xs text-muted-foreground">
                  <div className="flex flex-wrap items-center gap-1">
                    {article.tags &&
                      article.tags.length > 0 &&
                      article.tags.map((tag) => (
                        <Link
                          key={`header-tag-${tag.id}`}
                          href={`/tag/${tag.slug}`}
                          className="px-2 py-0.5 rounded-md bg-gray-100 text-[11px] text-foreground hover:bg-gray-200 transition-colors"
                        >
                          {tag.name}
                        </Link>
                      ))}
                  </div>

                  {article.created_at && (
                    <div className="flex items-center gap-1 text-[11px] text-muted-foreground ml-auto">
                      <Clock className="h-3 w-3" />
                      <span>{formatDaysSinceCreated(article.created_at)}</span>
                    </div>
                  )}
                </div>

                {article.author && (
                  <div className="w-full mb-3 space-y-2 text-xs text-muted-foreground">
                    <div className="flex items-center gap-2 rounded-full bg-gray-100 px-4 py-2">
                      <div className="flex items-center justify-center h-7 w-7 rounded-full bg-red-100 text-red-600 text-[10px] font-semibold">
                        {article.author.name
                          .split(" ")
                          .filter(Boolean)
                          .slice(0, 2)
                          .map((part) => part[0]?.toUpperCase())
                          .join("")}
                      </div>
                      <span className="text-xs font-medium text-foreground">
                        {article.author.name}
                      </span>
                    </div>

                    <div className="flex items-center gap-3 border border-gray-200 -mx-4 md:-mx-6">
                      <div
                        className="flex items-center justify-center px-2 py-2 bg-[#B01622] text-white text-[11px] font-semibold"
                        style={{ writingMode: "vertical-rl", textOrientation: "mixed" }}
                      >
                        Share
                      </div>
                      <div className="flex items-center gap-2 px-3 py-2">
                        <a
                          href={
                            shareUrl
                              ? `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                                  shareUrl,
                                )}`
                              : "#"
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="h-7 w-7 rounded-full flex items-center justify-center bg-[#1877F2] text-white"
                        >
                          <Facebook className="h-4 w-4" />
                        </a>
                        <a
                          href={
                            shareUrl
                              ? `https://twitter.com/intent/tweet?url=${encodeURIComponent(
                                  shareUrl,
                                )}`
                              : "#"
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="h-7 w-7 rounded-full flex items-center justify-center bg-[#1DA1F2] text-white"
                        >
                          <Twitter className="h-4 w-4" />
                        </a>
                        <a
                          href={
                            shareUrl
                              ? `https://api.whatsapp.com/send?text=${encodeURIComponent(
                                  shareUrl,
                                )}`
                              : "#"
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="h-7 w-7 rounded-full flex items-center justify-center bg-[#25D366] text-white"
                        >
                          <MessageCircle className="h-4 w-4" />
                        </a>
                        <a
                          href={
                            shareUrl
                              ? `mailto:?subject=${encodeURIComponent(
                                  article.title,
                                )}&body=${encodeURIComponent(shareUrl)}`
                              : "#"
                          }
                          className="h-7 w-7 rounded-full flex items-center justify-center bg-[#FDBD01] text-white"
                        >
                          <Mail className="h-4 w-4" />
                        </a>
                      </div>
                    </div>
                  </div>
                )}

                {article.input_images && article.input_images.length > 0 && (
                  <div className="mb-4">
                    <ImageSlider images={article.input_images} alt={article.title} />
                  </div>
                )}
              </div>

              <div className="mt-4">
                <ArticleContent content={processedContent || article.content || ""} />
              </div>

              {latestNews.length > 0 && (
                <div className="mt-8">
                  <div
                    className="flex items-center rounded-md mb-4"
                    style={{
                      backgroundColor: "#4a4a4a",
                      borderRadius: "6px",
                      padding: "8px 12px",
                      position: "relative",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: "4px",
                        backgroundColor: "#D00501",
                        borderTopLeftRadius: "6px",
                        borderBottomLeftRadius: "6px",
                      }}
                    />
                    <span
                      className="font-semibold"
                      style={{
                        fontSize: "14px",
                        color: "#FFFFFF",
                        marginLeft: "8px",
                      }}
                    >
                      Home Feed
                    </span>
                  </div>
                  <LatestNewsList articles={articleHomeFeedDisplayed} />
                  {showArticleHomeFeedMore && (
                    <div className="flex justify-center mt-4">
                      <Button
                        type="button"
                        variant="outline"
                        className="min-w-[140px]"
                        onClick={() => setHomeFeedExpanded(true)}
                      >
                        Show more
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </main>

          <aside className="col-span-12 lg:col-span-4 lg:sticky lg:top-28 self-start h-fit">
            <ViralNewsSidebar
              importantArticles={importantNews}
              viralArticles={viralNews}
              onLoadMoreImportant={loadMoreImportant}
              hasMoreImportant={hasMoreImportant}
            />
          </aside>
        </div>
      </div>
    </HomePageLayout>
  )
}

